import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';

import U2000BasicTable, {
  type U2000SearchPayload,
  type U2000SearchResult,
} from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000IdColumnDef, type U2000TableColumn } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import React, { useEffect, useRef } from 'react';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import { useU2000QuickSearchListEffect } from '../../u2000Core/hooks/useU2000QuickSearchListEffect';
import { useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';

import type { U2000TableHandlers } from '../../u2000Core/types/U2000TableHandlers';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2201Context } from '../U2201Context';
import { doSearch, luHandleAppiError, type U2201JobScheduleSearchRow } from '../U2201Service';
import { NS } from '../U2201';
import { U2201JobScheduleListToolBar } from './list/U2201JobScheduleListToolBar';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from '../../u2000Core/tools/U2000QuickSearchUtils';

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: 'scheduleCode',
      label: t('lblScheduleCode', { ns: NS }),
      widthClass: 'w-[200px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      isDefaultSortedColumn: true,
    },
    {
      name: 'jobType',
      label: t('lblJobType', { ns: NS }),
      widthClass: 'w-[220px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
    {
      name: 'enabled',
      label: t('lblEnabled', { ns: NS }),
      widthClass: 'w-[100px]',
      type: 'boolean',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
    {
      name: 'cronExpression',
      label: t('lblCronExpression', { ns: NS }),
      widthClass: 'w-[140px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
    {
      name: 'timezone',
      label: t('lblTimezone', { ns: NS }),
      widthClass: 'w-[160px]',
      type: 'string',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
    {
      name: 'nextRunAt',
      label: t('lblNextRunAt', { ns: NS }),
      widthClass: 'w-[180px]',
      type: 'date',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      includeConditions: [
        'equals',
        'notequals',
        'greaterthan',
        'greaterthanorequal',
        'lesthan',
        'lesthanorequal',
        'interval',
      ],
    },
    {
      name: 'lastRunAt',
      label: t('lblLastRunAt', { ns: NS }),
      widthClass: 'w-[180px]',
      type: 'date',
      align: 'left',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
      includeConditions: [
        'equals',
        'notequals',
        'greaterthan',
        'greaterthanorequal',
        'lesthan',
        'lesthanorequal',
        'interval',
      ],
    },
    {
      name: 'maxAttempts',
      label: t('lblMaxAttempts', { ns: NS }),
      widthClass: 'w-[110px]',
      type: 'number',
      align: 'center',
      isCriteriaField: true,
      inGridField: true,
      isDefaultColumn: true,
      sortable: true,
    },
  ];
}

const U2201JobScheduleList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();

  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU2201Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);

  useEffect(() => {
    const listTitle = t('title', { ns: NS });
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: listTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchStateHandlers / gridStateHandlers instables
  }, [t]);

  const handleSearch = useU2000SearchHandlers<U2201JobScheduleSearchRow>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (
      searchResult: U2000SearchResult<U2201JobScheduleSearchRow>,
      direction: U2000GridStateEnumPaginationDirection
    ) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? '/U2201';
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const single = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom('');
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === single.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, single.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- erreur axios
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const u2201ListHandlerActions: U2000TableHandlers<U2201JobScheduleSearchRow> = {
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(
        searchStateHandlers.extractSearchParameter(),
        gridStateHandlers.extractPagination(page),
        direction
      );
    },
    onRowClick: (row: U2201JobScheduleSearchRow) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom('');
      const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === row.id);
      gridStateHandlers.setCurrentIdx(currentIdx);
      navigate(`${row.id}`);
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom('/U2201');
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
  };

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: '/U2201',
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <U2000BasicTable
      searchStateHandlers={searchStateHandlers}
      gridStateHandlers={gridStateHandlers}
      actionsButtons={
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- toolbar identique U3106
        <U2201JobScheduleListToolBar
          gridStateHandlers={gridStateHandlers as any}
          handlerActions={u2201ListHandlerActions as any}
        />
      }
      handlerActions={u2201ListHandlerActions}
      label={t('lblJobScheduleList', {
        count: gridStateHandlers.getTotalRows(),
        ns: NS,
      })}
      allowRowSelection={true}
      hasExportRole={false}
    />
  );
};

export default U2201JobScheduleList;
